import {useRouteStore} from '@toolify/client/src/stores/RouteStore/useRouteStore'
import {Pinia} from 'pinia'
import {isNavigationFailure, RouteLocationNormalized} from 'vue-router'

export function handleStackableRouteRouterHookFactory(pinia: Pinia) {
  const routeStore = useRouteStore(pinia)

  return function handleStackableRouteRouterHook(to: RouteLocationNormalized, from: RouteLocationNormalized, failure?) {
    if(isNavigationFailure(failure)) {
      return
    }
    const newHistoryPosition = window.history?.state?.position || 0
    const adjustedNewPosition = newHistoryPosition - 1
    const previousHistoryPosition = routeStore.currentHistoryPosition ?? -1
    routeStore.currentHistoryPosition = adjustedNewPosition

    if(!from) {
      routeStore.setLastNonStackableRoute(to)
    }
    let isStackable = to.meta?.isStackable
    if (Boolean(isStackable) && typeof to.meta?.isStackableCondition === 'function') {
      isStackable = to.meta.isStackableCondition(to, from)
    }

    if(!isStackable) {
      routeStore.setLastNonStackableRoute(to)
      routeStore.setStackableRoutes([])
      routeStore.setStackableLevelDepths([])
      return
    }

    const fromIsRealRoute = (from?.matched?.length ?? 0) > 0

    if(!routeStore.lastNonStackableRoute) {
      if(!fromIsRealRoute) {
        routeStore.setLastNonStackableRoute(to)
        routeStore.setStackableRoutes([])
        routeStore.setStackableLevelDepths([])
        return
      }
      routeStore.setLastNonStackableRoute(from)
    }

    const baseFullPath = routeStore.lastNonStackableRoute?.fullPath

    if(to.fullPath === baseFullPath) {
      routeStore.setStackableRoutes([])
      routeStore.setStackableLevelDepths([])
      return
    }

    if(
      fromIsRealRoute &&
      routeStore.stackableRoutes.length === 0 &&
      from.fullPath === baseFullPath &&
      Boolean(to.name) &&
      from.name === to.name &&
      to.path === from.path
    ) {
      routeStore.setLastNonStackableRoute(to)
      return
    }

    const topStackableRoute = routeStore.stackableRoutes[routeStore.stackableRoutes.length - 1]
    const isSameStackableLevel = Boolean(topStackableRoute) && Boolean(to.name) && topStackableRoute.name === to.name
    if(isSameStackableLevel) {
      routeStore.setStackableRoutes([
        ...routeStore.stackableRoutes.slice(0, -1),
        to,
      ] as RouteLocationNormalized[])
      const navigationDirection = adjustedNewPosition > previousHistoryPosition
        ? 1
        : adjustedNewPosition < previousHistoryPosition
          ? -1
          : 0
      const updatedDepths = routeStore.stackableLevelDepths.slice()
      if(updatedDepths.length > 0) {
        const topIndex = updatedDepths.length - 1
        updatedDepths[topIndex] = Math.max(0, (updatedDepths[topIndex] ?? 0) + navigationDirection)
        routeStore.setStackableLevelDepths(updatedDepths)
      }
      return
    }

    if(previousHistoryPosition > adjustedNewPosition) {
      routeStore.setStackableRoutes(routeStore.stackableRoutes.slice(0, -1) as RouteLocationNormalized[])
      routeStore.setStackableLevelDepths(routeStore.stackableLevelDepths.slice(0, -1))
      return
    }

    if(previousHistoryPosition < adjustedNewPosition) {
      routeStore.setStackableRoutes(
        routeStore.stackableRoutes.concat([to]) as RouteLocationNormalized[],
      )
      routeStore.setStackableLevelDepths(routeStore.stackableLevelDepths.concat([0]))
      return
    }

    return
  }
}
